import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { UsersIcon } from 'lucide-react';
import {
  BackLink,
  Button,
  CardHeader,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  Field,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
  TextInput,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { Checkbox } from '@/components/ui/checkbox';
import { errorMessage } from '@/lib/api';
import { readPermissionCatalog, type PermissionCatalogEntry } from '@/lib/events';
import { sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import {
  ROLE_QUERY_KEY,
  archiveRole,
  canManageRoles,
  memberName,
  readRole,
  updateRole,
  useMyIdentity,
  type RoleDetail,
} from '@/lib/roles';

/** Where the Roles list lives. */
const ROLES_TAB_PATH = '/settings?tab=roles';

/** Field lengths, matching the API's. */
const ROLE_NAME_MAX = 60;
const ROLE_DESCRIPTION_MAX = 240;

/** Member table sort, kept apart from any other table's params. */
const MEMBER_SORT_FIELDS = { name: 'name', email: 'email' } as const;
const MEMBER_SORT_KEYS = Object.values(MEMBER_SORT_FIELDS);
const MEMBER_DEFAULT_SORT: DataTableSort = { key: MEMBER_SORT_FIELDS.name, direction: 'asc' };
const MEMBER_SORT_PREFIX = 'members';

/** The catalog grouped under its headings, in catalog order. */
function groupCatalog(catalog: readonly PermissionCatalogEntry[]): ReadonlyArray<{
  readonly label: string;
  readonly entries: readonly PermissionCatalogEntry[];
}> {
  const groups = new Map<string, { label: string; entries: PermissionCatalogEntry[] }>();
  for (const entry of catalog) {
    const group = groups.get(entry.group) ?? { label: entry.groupLabel, entries: [] };
    group.entries.push(entry);
    groups.set(entry.group, group);
  }
  return [...groups.values()];
}

/**
 * One role: its name, the permissions it carries and the people who hold it.
 * Permissions the viewer does not hold are shown but locked, because the API
 * refuses changing them. Saving sends the version this page read; if someone
 * else saved first, the page says so instead of overwriting their change.
 *
 * @returns The role page.
 */
export function RolePage(): React.ReactElement {
  const { roleId = '' } = useParams();
  const identity = useMyIdentity();
  // The version this page last saved, so the remounted editor can say "Saved".
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const allowed = canManageRoles(identity.data);
  const role = useQuery({
    queryKey: ROLE_QUERY_KEY.detail(roleId),
    queryFn: ({ signal }) => readRole(roleId, signal),
    enabled: allowed,
  });
  const catalog = useQuery({
    queryKey: ['permission-catalog'],
    queryFn: ({ signal }) => readPermissionCatalog(signal),
    staleTime: 5 * 60_000,
    enabled: allowed,
  });

  if (identity.isPending) return <LoadingState label="Loading…" />;
  if (!allowed) return <Navigate to="/settings" replace />;

  return (
    <section className="animate-portal-rise">
      <BackLink to={ROLES_TAB_PATH}>Roles</BackLink>
      {role.isError || catalog.isError ? (
        <ErrorNotice title="Role unavailable" message={errorMessage(role.error ?? catalog.error)} />
      ) : role.isPending || catalog.isPending ? (
        <LoadingState label="Loading role…" />
      ) : (
        // Keyed on version so a save (or someone else's) resets the draft to what is stored.
        <RoleEditor
          key={`${role.data.id}:${role.data.version}`}
          role={role.data}
          catalog={catalog.data}
          justSaved={savedVersion === role.data.version}
          onSaved={setSavedVersion}
        />
      )}
    </section>
  );
}

function RoleEditor({
  role,
  catalog,
  justSaved,
  onSaved,
}: {
  readonly role: RoleDetail;
  readonly catalog: readonly PermissionCatalogEntry[];
  /** This page saved the version showing, and nothing has changed since. */
  readonly justSaved: boolean;
  readonly onSaved: (version: number) => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');
  const [permissions, setPermissions] = useState<ReadonlySet<string>>(new Set(role.permissions));
  const [archiving, setArchiving] = useState(false);

  const dirty =
    name.trim() !== role.name ||
    description.trim() !== (role.description ?? '') ||
    permissions.size !== role.permissions.length ||
    role.permissions.some((permission) => !permissions.has(permission));

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ROLE_QUERY_KEY.detail(role.id) }),
      queryClient.invalidateQueries({ queryKey: ROLE_QUERY_KEY.list }),
    ]);
  }

  const save = useMutation({
    mutationFn: () =>
      updateRole(role.id, {
        version: role.version,
        name: name.trim(),
        description: description.trim(),
        permissions: catalog.map((entry) => entry.key).filter((key) => permissions.has(key)),
      }),
    onSuccess: async (updated) => {
      onSaved(updated.version);
      await refresh();
    },
  });
  const archive = useMutation({
    mutationFn: () => archiveRole(role.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ROLE_QUERY_KEY.list });
      navigate(ROLES_TAB_PATH);
    },
  });

  function toggle(key: string, on: boolean): void {
    const next = new Set(permissions);
    if (on) next.add(key);
    else next.delete(key);
    setPermissions(next);
  }

  // You can archive only a role whose permissions you all hold (the API's rule).
  const heldKeys = new Set(catalog.filter((entry) => entry.held).map((entry) => entry.key));
  const canArchive = !role.system && role.permissions.every((permission) => heldKeys.has(permission));

  return (
    <>
      <PageHeading
        eyebrow={role.system ? 'BUILT-IN ROLE' : 'ROLE'}
        title={role.name}
        subtitle={role.description ?? 'A named set of permissions given to people.'}
      />

      <div className="mt-6 space-y-5">
        <Panel className="p-5">
          <CardHeader title="Details" description="How the role reads on people's profiles." />
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Name" htmlFor="role-name" required>
              <TextInput id="role-name" value={name} maxLength={ROLE_NAME_MAX} onChange={(event) => setName(event.target.value)} />
            </Field>
            <Field label="Description" htmlFor="role-description">
              <TextInput
                id="role-description"
                value={description}
                maxLength={ROLE_DESCRIPTION_MAX}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Field>
          </div>
        </Panel>

        <Panel className="overflow-hidden">
          <div className="p-5 pb-4">
            <CardHeader
              className="mb-0"
              title="Permissions"
              description="What everyone with this role can do. You can add or remove only permissions you hold yourself."
            />
          </div>
          {groupCatalog(catalog).map((group) => (
            <div key={group.label} className="border-t border-portal-line">
              <h3 className="bg-portal-bg px-5 py-2 text-portal-micro font-bold uppercase tracking-wide text-portal-muted">
                {group.label}
              </h3>
              <ul className="divide-y divide-portal-line">
                {group.entries.map((entry) => {
                  const id = `role-permission-${entry.key}`;
                  return (
                    <li key={entry.key} className="flex items-start gap-3 px-5 py-3">
                      <Checkbox
                        id={id}
                        checked={permissions.has(entry.key)}
                        disabled={save.isPending || !entry.held}
                        onCheckedChange={(state) => toggle(entry.key, state === true)}
                        className="mt-0.5"
                      />
                      <label htmlFor={id} className={entry.held ? 'min-w-0 flex-1 cursor-pointer' : 'min-w-0 flex-1'}>
                        <span className="flex flex-wrap items-center gap-2 text-portal-small font-bold">
                          {entry.label}
                          {entry.sensitive ? <Pill tone="warning">Sensitive</Pill> : null}
                        </span>
                        <span className="block text-portal-micro text-portal-muted">{entry.description}</span>
                        {entry.held ? null : (
                          <span className="block text-portal-micro text-portal-muted">You don't hold this one, so you can't change it.</span>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </Panel>

        {save.isError ? <ErrorNotice inline title="Not saved" message={errorMessage(save.error)} /> : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button disabled={save.isPending || !dirty || name.trim().length === 0} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="secondary"
            disabled={save.isPending || !dirty}
            onClick={() => {
              setName(role.name);
              setDescription(role.description ?? '');
              setPermissions(new Set(role.permissions));
            }}
          >
            Reset
          </Button>
          {justSaved && !dirty ? <span className="text-portal-micro font-bold text-emerald-600">Saved</span> : null}
          {canArchive ? (
            <span className="ml-auto">
              <Button variant="secondary" onClick={() => setArchiving(true)}>
                Archive role
              </Button>
            </span>
          ) : null}
        </div>

        <MembersCard role={role} />
      </div>

      <ConfirmDialog
        open={archiving}
        title={`Archive ${role.name}?`}
        message={
          role.memberCount === 0
            ? 'Nobody holds this role, so nothing changes for anyone. It leaves the Roles list.'
            : `${role.memberCount} ${role.memberCount === 1 ? 'person loses' : 'people lose'} what this role gives them, unless another role or their own permissions still grant it.`
        }
        confirmLabel="Archive"
        tone="danger"
        pending={archive.isPending}
        error={archive.isError ? errorMessage(archive.error) : undefined}
        onConfirm={() => archive.mutate()}
        onCancel={() => setArchiving(false)}
      />
    </>
  );
}

/** The people holding the role; roles are given from each person's profile. */
function MembersCard({ role }: { readonly role: RoleDetail }): React.ReactElement {
  const { sort, onSort } = useTableSort(MEMBER_SORT_KEYS, MEMBER_DEFAULT_SORT, { prefix: MEMBER_SORT_PREFIX });
  const rows = sortRows(
    role.members.map((member) => ({ ...member, name: memberName(member) })),
    sort,
  );
  return (
    <div>
      <h2 className="mb-1 text-portal-section font-bold tracking-[-0.4px]">People with this role</h2>
      <p className="mb-3 text-portal-small text-portal-muted">Give someone a role from their profile.</p>
      {rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<UsersIcon aria-hidden className="size-6" />}
            title="Nobody has this role yet"
            message="Open someone's profile under People and give them this role there."
            action={
              <Link to="/people" className="text-portal-small font-bold text-brand hover:underline">
                Go to People
              </Link>
            }
          />
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Name', sortKey: MEMBER_SORT_FIELDS.name },
            { label: 'Email', sortKey: MEMBER_SORT_FIELDS.email },
          ]}
          sort={sort}
          onSort={onSort}
        >
          {rows.map((member) => (
            <tr key={member.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <Link to={`/people/${member.id}`} className="font-bold text-brand hover:underline">
                  {member.name}
                </Link>
              </td>
              <td className="p-3 text-portal-small text-portal-muted">{member.email}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );
}
